import React from 'react';
import { CheckCircle2, X } from 'lucide-react';

const WhyHumanLed = () => {
  const comparisons = [
    { feature: 'Application Strategy', ai: 'Mass submissions', human: 'Strategic pacing' },
    { feature: 'Content Quality', ai: 'Generic language', human: 'Role-specific tailoring' },
    { feature: 'ATS Optimization', ai: 'Keyword stuffing', human: 'ATS + human balance' },
    { feature: 'Accountability', ai: 'No accountability', human: 'Recruiter oversight' },
    { feature: 'Follow-Up', ai: 'No follow-up', human: 'Visibility support' }
  ];

  const benefits = [
    {
      title: 'Human-Led Applications',
      description: 'No bots. No shortcuts. Applications submitted by real recruiters not automation.'
    },
    {
      title: 'Role-Specific Resumes',
      description: 'No generic resumes. Every role gets its own customized version.'
    },
    {
      title: 'ATS-Optimized Strategy',
      description: 'Documents optimized for ATS and AI Bots. Built to stand out to Hiring Managers.'
    },
    {
      title: 'Proven Expertise',
      description: '20+ years of professional experience from entry level to C-suite executives.'
    }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-teal-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold mb-4">
            Our Approach
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Why Human-Led Beats AI
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            This isn't bulk applying. It's strategic representation with real recruiter insight, not automation shortcuts.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-lg hover:border-teal-200 transition-all"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle2 className="text-teal-700" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-gray-600 text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="p-8">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
              How Our Service Stacks Up
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-6 text-gray-700 font-semibold">Feature</th>
                    <th className="text-center py-4 px-6 text-gray-700 font-semibold">
                      <div className="flex flex-col items-center">
                        <X className="text-red-500 mb-2" size={24} />
                        <span>AI Tools</span>
                      </div>
                    </th>
                    <th className="text-center py-4 px-6 text-teal-700 font-semibold">
                      <div className="flex flex-col items-center">
                        <CheckCircle2 className="text-teal-700 mb-2" size={24} />
                        <span>Apply Like A Pro™</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisons.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6 font-medium text-gray-900">{item.feature}</td>
                      <td className="py-4 px-6 text-center">
                        <div className="inline-flex items-center justify-center space-x-2">
                          <X className="text-red-500" size={18} />
                          <span className="text-gray-600">{item.ai}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="inline-flex items-center justify-center space-x-2">
                          <CheckCircle2 className="text-teal-700" size={18} />
                          <span className="text-gray-900 font-medium">{item.human}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-amber-50 border border-amber-200 rounded-xl p-6 max-w-3xl">
            <p className="text-lg text-gray-800">
              <span className="font-bold text-amber-900">AI-generated applications</span> are now one of the biggest reasons qualified candidates are being overlooked. Hiring teams notice—and they're filtering them out.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyHumanLed;